import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  CircularProgress,
  IconButton,
  alpha,
  Tooltip,
  Chip,
  Stack,
} from '@mui/material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { getAllUsers, getClubs } from '../../Utils/apiServices';
import RefreshIcon from '@mui/icons-material/Refresh';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupIcon from '@mui/icons-material/Group';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

const STAT_CONFIGS = [
  {
    key: 'totalUsers',
    title: 'Total Users',
    icon: PeopleAltIcon,
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    shadow: 'rgba(59,130,246,0.4)',
    trend: 15,
  },
  {
    key: 'newSignups',
    title: 'New Signups',
    icon: PersonAddIcon,
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    shadow: 'rgba(16,185,129,0.4)',
    trend: 8,
  },
  {
    key: 'activeUsers',
    title: 'Active Users',
    icon: GroupIcon,
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    shadow: 'rgba(245,158,11,0.4)',
    trend: -5,
  },
  {
    key: 'avgSessionDuration',
    title: 'Avg. Session (mins)',
    icon: AccessTimeIcon,
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
    shadow: 'rgba(139,92,246,0.4)',
    trend: 12,
  },
];

const PIE_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444'];
const BAR_COLORS = ['#6366f1', '#3b82f6', '#0ea5e9', '#06b6d4', '#10b981'];

const Dashboard = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('weekly');
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    newSignups: 0,
    activeUsers: 0,
    avgSessionDuration: 0,
  });

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersResponse, clubsResponse] = await Promise.all([
        getAllUsers(),
        getClubs(),
      ]);
      setUsers(usersResponse.data.data);
      setEvents(clubsResponse.data.data);
      calculateStats(usersResponse.data.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userData) => {
    const now = new Date();
    const timeRanges = { daily: 1, weekly: 7, monthly: 30 };
    const rangeInDays = timeRanges[timeRange];
    const rangeDate = new Date(now.getTime() - rangeInDays * 24 * 60 * 60 * 1000);
    const newUsers = userData.filter((user) => new Date(user.created_at) >= rangeDate).length;
    setStats({
      totalUsers: userData.length,
      newSignups: newUsers,
      activeUsers: Math.floor(userData.length * 0.7),
      avgSessionDuration: Math.floor(Math.random() * 30) + 15,
    });
  };

  const eventVisitsData = events.slice(0, 6).map((club) => ({
    name: club.name?.length > 12 ? club.name.slice(0, 12) + '…' : (club.name || 'Club'),
    visits: Math.floor(Math.random() * 1000) + 100,
  }));

  const userGrowthData = [
    { month: 'Jan', users: 65 },
    { month: 'Feb', users: 78 },
    { month: 'Mar', users: 90 },
    { month: 'Apr', users: 115 },
    { month: 'May', users: 130 },
    { month: 'Jun', users: stats.totalUsers },
  ];

  const userTypeData = [
    { name: 'Riders', value: users.filter((u) => u.user_type === 'Rider').length },
    { name: 'Admins', value: users.filter((u) => u.user_type === 'Admin').length },
    { name: 'Other', value: users.filter((u) => u.user_type !== 'Rider' && u.user_type !== 'Admin').length },
  ].filter((d) => d.value > 0);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 320, gap: 2 }}>
        <CircularProgress size={48} thickness={4} sx={{ color: theme.palette.primary.main }} />
        <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Loading dashboard…
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ transition: 'all 0.3s ease-in-out' }}>
      {/* Header bar */}
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'background.paper',
          borderRadius: 3,
          p: 2.5,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          background: 'linear-gradient(135deg, #fff 0%, #f8faff 100%)',
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: 'text.primary', letterSpacing: '-0.02em', mb: 0.4 }}
          >
            Dashboard Overview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track your platform's performance and user engagement
          </Typography>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Stat cards — fully colored */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {STAT_CONFIGS.map(({ key, title, icon: Icon, gradient, shadow, trend }) => {
          const positive = trend > 0;
          return (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <Card
                sx={{
                  background: gradient,
                  borderRadius: 3,
                  boxShadow: `0 8px 24px ${shadow}`,
                  border: 'none',
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                  transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 16px 40px ${shadow}`,
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -30,
                    right: -30,
                    width: 110,
                    height: 110,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.12)',
                  },
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -20,
                    right: 20,
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                    <Box
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: 2,
                        p: 1.25,
                        display: 'flex',
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      <Icon sx={{ fontSize: 24, color: '#fff' }} />
                    </Box>
                    <Chip
                      icon={positive ? <TrendingUpIcon sx={{ fontSize: 14, color: 'inherit !important' }} /> : <TrendingDownIcon sx={{ fontSize: 14, color: 'inherit !important' }} />}
                      label={`${Math.abs(trend)}%`}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        height: 24,
                        backdropFilter: 'blur(4px)',
                        '& .MuiChip-icon': { color: '#fff' },
                      }}
                    />
                  </Box>
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#fff', mb: 0.5, letterSpacing: '-1px' }}>
                    {stats[key].toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                    {title}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2.5}>
        {/* Bar chart */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.05)',
              height: '100%',
              background: 'linear-gradient(135deg, #fff 0%, #fafbff 100%)',
              transition: 'box-shadow 0.25s',
              '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.1)' },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.3 }}>
                    Top Club Visits
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Most popular clubs based on visitor count
                  </Typography>
                </Box>
                <Tooltip title="Refresh" arrow>
                  <IconButton
                    onClick={fetchData}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                    }}
                  >
                    <RefreshIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={eventVisitsData} barSize={32}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      borderRadius: 10,
                      border: 'none',
                      color: '#fff',
                      fontSize: 13,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    }}
                    cursor={{ fill: 'rgba(99,102,241,0.08)' }}
                  />
                  <Bar dataKey="visits" radius={[6, 6, 0, 0]}>
                    {eventVisitsData.map((_, i) => (
                      <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Pie chart */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.05)',
              height: '100%',
              background: 'linear-gradient(135deg, #fff 0%, #fafbff 100%)',
              transition: 'box-shadow 0.25s',
              '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.1)' },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.3 }}>
                    User Types
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Breakdown by role
                  </Typography>
                </Box>
                <Tooltip title="Refresh" arrow>
                  <IconButton
                    onClick={fetchData}
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                      '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                    }}
                  >
                    <RefreshIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              </Box>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={userTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={100}
                    innerRadius={55}
                    dataKey="value"
                    paddingAngle={4}
                  >
                    {userTypeData.map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      borderRadius: 10,
                      border: 'none',
                      color: '#fff',
                      fontSize: 13,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    }}
                  />
                  <Legend
                    verticalAlign="bottom"
                    height={32}
                    formatter={(value) => <span style={{ color: '#64748b', fontSize: 12 }}>{value}</span>}
                  />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Line chart */}
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              border: '1px solid rgba(0,0,0,0.05)',
              background: 'linear-gradient(135deg, #fff 0%, #fafbff 100%)',
              transition: 'box-shadow 0.25s',
              '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.1)' },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.3 }}>
                    User Growth Trend
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly user acquisition and retention
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#10b981' }} />
                  <Typography variant="caption" color="text.secondary">Users</Typography>
                  <Tooltip title="Refresh" arrow>
                    <IconButton
                      onClick={fetchData}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.16) },
                      }}
                    >
                      <RefreshIcon fontSize="small" color="primary" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Box>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={userGrowthData}>
                  <defs>
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="100%" stopColor="#3b82f6" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: '#1e293b',
                      borderRadius: 10,
                      border: 'none',
                      color: '#fff',
                      fontSize: 13,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="users"
                    stroke="url(#lineGradient)"
                    strokeWidth={3}
                    dot={{ r: 5, strokeWidth: 0, fill: '#10b981' }}
                    activeDot={{ r: 7, strokeWidth: 0, fill: '#3b82f6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
